import { useSavedLocationsApi } from "@/Hooks/useSavedLocationsApi";
import { useUsersApi } from "@/Hooks/useUsersApi";
import type { DialogBaseProps } from "@freightsimple/ui-core/Dialog/DialogBaseProps";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { Banner } from "@freightsimple/ui-core/Display/Banner";
import { ControlPresentationProvider } from "@freightsimple/ui-core/Controls/ControlPresentationProvider";
import { Dialog } from "@freightsimple/ui-core/Dialog/Dialog";
import { Spinner } from "@freightsimple/ui-core/Feedback/Spinner";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import BranchesDropdown from "@/Shared/Branches/Dropdowns/BranchesDropdown";
import type { User } from "@freightsimple/generated-dashboard-openapi-client/models/User";
import type { SavedLocation } from "@freightsimple/generated-dashboard-openapi-client/models/SavedLocation";
import { useEffect, useState } from "react";

interface ViewCompanyChangeBranchDialogProps extends DialogBaseProps {
  user: User;
  /** Fired with the updated user once the branch change has been saved. */
  onDone: (updatedUser: User) => void;
}

// Assign-user-to-branch dialog. Replaces the legacy imperative modal (the old
// width-morphing chrome is dropped; the dialog shows a spinner while loading,
// and a failed load or save shows inline).
export function ViewCompanyChangeBranchDialog(
  props: ViewCompanyChangeBranchDialogProps,
) {
  const usersApi = useUsersApi();
  const savedLocationsApi = useSavedLocationsApi();

  const [changing, setChanging] = useState(false);
  const [branchId, setBranchId] = useState(props.user.branchId);
  const [dirty, setDirty] = useState(false);
  const [branches, setBranches] = useState<undefined | SavedLocation[]>(
    undefined,
  );
  const [branchesLoadFailed, setBranchesLoadFailed] = useState(false);
  const [assignFailed, setAssignFailed] = useState(false);

  function loadBranches() {
    setBranchesLoadFailed(false);
    savedLocationsApi.getBranches().then(
      (response) => setBranches(response.items),
      (e) => {
        console.error(e);
        setBranchesLoadFailed(true);
      },
    );
  }

  useEffect(() => {
    if (!props.open) {
      return;
    }
    setBranchId(props.user.branchId);
    setDirty(false);
    setAssignFailed(false);
    loadBranches();
  }, [props.open]);

  async function onChange() {
    try {
      setChanging(true);
      setAssignFailed(false);

      // Magic string
      const response =
        branchId === undefined || branchId === "no_branch_assigned"
          ? await usersApi.clearBranch({
              clearBranchRequest: {
                userId: props.user.userId,
              },
            })
          : await usersApi.setBranch({
              setBranchRequest: {
                userId: props.user.userId,
                locationId: branchId,
              },
            });

      setChanging(false);
      props.onOpenChange?.(false);
      props.onDone(response);
    } catch (e) {
      console.error("Something went wrong changing the branch", { e });
      setAssignFailed(true);
      setChanging(false);
    }
  }

  return (
    <Dialog
      open={props.open}
      onOpenChange={props.onOpenChange}
      title="Assign to branch"
      description="Which branch would you like to assign this user to?"
      width="narrow"
      footer={
        <>
          <Button size="large" onClick={() => props.onOpenChange?.(false)}>
            Cancel
          </Button>
          <Button
            primary
            data-testid="branches-assign-button"
            disabled={!dirty}
            disabledMessage="Pick a different branch to assign"
            size="large"
            onClick={onChange}
            loading={changing}
          >
            Assign
          </Button>
        </>
      }
    >
      <ControlPresentationProvider presentation="underlined">
        {branchesLoadFailed ? (
          <Banner
            variant="danger"
            title="We couldn't load your branches"
            actions={
              <Button onClick={loadBranches} data-testid="branches-retry">
                Try again
              </Button>
            }
          >
            Something went wrong on our end. Please try again.
          </Banner>
        ) : branches === undefined ? (
          <Stack align="center">
            <Spinner size={32} />
          </Stack>
        ) : (
          <Stack align="center" gap={4}>
            {assignFailed && (
              <Banner variant="danger">
                We couldn&apos;t assign this branch. Please try again.
              </Banner>
            )}
            <BranchesDropdown
              branchId={branchId}
              setBranchId={function (newBranchId) {
                if (newBranchId !== branchId) {
                  setDirty(true);
                  setBranchId(newBranchId);
                }
              }}
              branches={branches}
            />
          </Stack>
        )}
      </ControlPresentationProvider>
    </Dialog>
  );
}
